(() => {
  const selector = '[data-cv-lorex-cnx3-turret-controller]';
  const controllers = new WeakMap();

  class CVLorexCNX3TurretPurchase {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.variants = this.readJson('[data-cv-lorex-cnx3-turret-variants]');
      this.gallery = this.readJson('[data-cv-lorex-cnx3-turret-gallery]');
      this.optionGroups = [...root.querySelectorAll('[data-cv-lorex-cnx3-turret-option]')];
      this.form = root.querySelector('[data-cv-lorex-cnx3-turret-product-form]');
      this.variantInput = root.querySelector('[data-cv-lorex-cnx3-turret-variant-input]');
      this.quantityInput = root.querySelector('[data-cv-lorex-cnx3-turret-quantity]');
      this.heroImage = root.querySelector('[data-cv-lorex-cnx3-turret-hero-image]');
      this.mobileSource = root.querySelector('[data-cv-lorex-cnx3-turret-mobile-source]');
      this.addButton = root.querySelector('[data-cv-lorex-cnx3-turret-add]');
      this.dialog = root.querySelector('[data-cv-lorex-cnx3-turret-dialog]');
      this.dialogImage = root.querySelector('[data-cv-lorex-cnx3-turret-gallery-image]');
      this.dialogCaption = root.querySelector('[data-cv-lorex-cnx3-turret-gallery-caption]');
      this.liveRegion = root.querySelector('[data-cv-lorex-cnx3-turret-live]');
      this.returnFocus = null;
      this.galleryIndex = 0;

      if (!this.form || !this.variantInput || !this.variants.length) return;
      this.bind();

      this.restoreFromUrl(false);
    }

    readJson(query) {
      const node = this.root.querySelector(query);
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]');
      } catch (_) {
        return [];
      }
    }

    bind() {
      const signal = this.abort.signal;

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-cv-lorex-cnx3-turret-option] input')) {
          this.update(this.findByOptions(), { historyMode: 'push', announce: true });
        }
        if (event.target.matches('[data-cv-lorex-cnx3-turret-quantity]')) { this.normalizeQuantity(); this.syncRelatedViews(); }
      }, { signal });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.normalizeQuantity();
        this.submitNativeFallback();
      }, { signal, capture: true });
      document.addEventListener('shopify:section:load', () => this.syncRelatedViews(), { signal });
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-cv-lorex-cnx3-turret-quantity-minus]')) {
          event.preventDefault();
          this.stepQuantity(-1);
          return;
        }
        if (event.target.closest('[data-cv-lorex-cnx3-turret-quantity-plus]')) {
          event.preventDefault();
          this.stepQuantity(1);
          return;
        }
        const galleryTrigger = event.target.closest('[data-cv-lorex-cnx3-turret-gallery-open]');
        if (galleryTrigger) {
          event.preventDefault();
          this.openGallery(galleryTrigger);
          return;
        }
        if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-close]')) this.closeGallery();
        if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-previous]')) this.showGallery(this.galleryIndex - 1);
        if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-next]')) this.showGallery(this.galleryIndex + 1);
      }, { signal });

      window.addEventListener('popstate', () => this.restoreFromUrl(false), { signal });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeGallery();
            return;
          }

          if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
            if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-close]')) {
              event.preventDefault();
              this.closeGallery();
              return;
            }
            if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-previous]')) {
              event.preventDefault();
              this.showGallery(this.galleryIndex - 1);
              return;
            }
            if (event.target.closest('[data-cv-lorex-cnx3-turret-gallery-next]')) {
              event.preventDefault();
              this.showGallery(this.galleryIndex + 1);
              return;
            }
          }

          if (event.key === 'ArrowLeft') this.showGallery(this.galleryIndex - 1);
          if (event.key === 'ArrowRight') this.showGallery(this.galleryIndex + 1);
          if (event.key === 'Tab') this.trapFocus(event);
        }, { signal });

        this.dialog.addEventListener('close', () => {
          this.dialogImage?.removeAttribute('src');
          document.documentElement.classList.remove('cv-lorex-cnx3-turret-dialog-open');
          this.returnFocus?.focus();
          this.returnFocus = null;
        }, { signal });
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.optionGroups.forEach((group) => { group.disabled = busy; });
      if (this.quantityInput) this.quantityInput.disabled = busy;
      this.root.querySelectorAll('[data-cv-lorex-cnx3-turret-quantity-minus], [data-cv-lorex-cnx3-turret-quantity-plus]').forEach((button) => { button.disabled = busy; });
      const button = this.root.querySelector('[data-cv-lorex-cnx3-turret-add]');
      if (button) {
        button.disabled = busy || !this.currentVariant?.available;
        button.setAttribute('aria-disabled', String(button.disabled));
        button.setAttribute('aria-busy', String(busy));
      }
      if (!busy) this.normalizeQuantity();
      this.syncRelatedViews();
    }

    async submitNativeFallback() {
      // One native Liquid form owns both native submit buttons and every input.
      if (this.busy || !this.currentVariant?.available) return;
      const formData = new FormData(this.form);
      const errorRegion = this.root.querySelector('[ref="addToCartTextError"]');
      if (errorRegion) errorRegion.textContent = '';
      this.setBusy(true);
      const base = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url || `${base}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: formData, credentials: 'same-origin',
        });
        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Unable to add this item. Please try again.');
        window.location.assign(window.Theme?.routes?.cart_url || `${base}cart`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to add this item. Please try again.';
        if (errorRegion) { errorRegion.textContent = message; errorRegion.focus(); }
        if (this.liveRegion) this.liveRegion.textContent = message;
        this.setBusy(false);
        document.querySelectorAll('[data-cv-lorex-cnx3-turret-final-status]').forEach((node) => { node.textContent = message; });
      }
    }

    syncRelatedViews() {
      const variant = this.currentVariant;
      if (!variant) return;
      document.querySelectorAll('[data-cv-lorex-cnx3-turret-final-cta]').forEach((cta) => {
        if (cta.dataset.productId !== this.root.dataset.productId) return;
        const price = cta.querySelector('[data-cv-lorex-cnx3-turret-final-price]');
        const sku = cta.querySelector('[data-cv-lorex-cnx3-turret-final-sku]');
        const selection = cta.querySelector('[data-cv-lorex-cnx3-turret-final-selection]');
        const image = cta.querySelector('[data-cv-lorex-cnx3-turret-final-image]');
        const button = cta.querySelector('[data-cv-lorex-cnx3-turret-final-add]');
        const status = cta.querySelector('[data-cv-lorex-cnx3-turret-final-status]');
        if (price) price.textContent = variant.priceFormatted;
        if (sku) sku.textContent = variant.sku;
        if (selection) selection.textContent = variant.title;
        if (image && variant.image) { image.src = variant.image; image.srcset = variant.srcset; image.sizes = '(min-width: 990px) 30vw, 85vw'; image.alt = variant.alt; }
        if (button) {
          button.disabled = this.busy || !variant.available;
          button.setAttribute('aria-disabled', String(button.disabled));
          button.setAttribute('aria-busy', String(Boolean(this.busy)));
          button.textContent = this.busy ? 'Adding…' : variant.available ? button.dataset.readyLabel : button.dataset.unavailableLabel;
        }
        if (status) status.textContent = this.busy ? 'Adding your selected configuration…' : `Selected: ${variant.title}. Quantity: ${this.quantityInput?.value || 1}.`;
      });
      document.querySelectorAll('[data-cv-lorex-cnx3-turret-camera-count]').forEach((node) => { node.textContent = `${variant.options[1]} · ${variant.options[0]}`; });
      const config = this.root.querySelector('[data-cv-lorex-cnx3-turret-config]');
      if (config) config.textContent = `${variant.options[1]} · ${variant.options[0]} ${config.dataset.label || ''}`.trim();
      document.querySelectorAll('[data-cv-lorex-cnx3-turret-detail-image]').forEach((image) => {
        const color = variant.options[0] === 'White' ? 'white' : 'black';
        if (image.dataset[color]) { image.src = image.dataset[color]; image.alt = image.dataset[`${color}Alt`] || image.alt; }
      });
    }

    selectedOptions() {
      return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
    }

    findByOptions() {
      const options = this.selectedOptions();
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    findById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    restoreFromUrl(announce) {
      const id = new URL(window.location.href).searchParams.get('variant') || this.variantInput.value;
      const variant = this.findById(id) || this.findByOptions() || this.variants[0];
      this.checkOptions(variant);
      this.update(variant, { historyMode: 'replace', announce });
    }

    checkOptions(variant) {
      if (!variant) return;
      this.optionGroups.forEach((group, index) => {
        const input = [...group.querySelectorAll('input')].find((node) => node.value === variant.options[index]);
        if (input) input.checked = true;
      });
    }

    update(variant, { historyMode = 'replace', announce = false } = {}) {
      if (!variant) return;
      this.currentVariant = variant;

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.form.querySelectorAll('shopify-payment-terms').forEach((terms) => {
        terms.setAttribute('variant-id', String(variant.id));
      });

      this.optionGroups.forEach((group) => {
        const output = group.querySelector('[data-cv-lorex-cnx3-turret-option-value]');
        const checked = group.querySelector('input:checked');
        if (output && checked) output.textContent = checked.value;
      });

      this.setText('[data-cv-lorex-cnx3-turret-price]', variant.priceFormatted);
      this.setText('[data-cv-lorex-cnx3-turret-sku]', variant.sku || '—');

      const compare = this.root.querySelector('[data-cv-lorex-cnx3-turret-compare]');
      if (compare) {
        compare.hidden = !(variant.compareAt > variant.price);
        compare.textContent = variant.compareAt > variant.price ? variant.compareAtFormatted : '';
      }

      const addButton = this.root.querySelector('[data-cv-lorex-cnx3-turret-add]');
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        const label = addButton.querySelector('[data-cv-lorex-cnx3-turret-add-label]');
        if (label) label.textContent = variant.available ? addButton.dataset.readyLabel : addButton.dataset.unavailableLabel;
      }

      this.updateQuantityRules(variant);
      this.updateHero(variant);
      this.syncRelatedViews();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      const state = { variantId: variant.id };
      if (historyMode === 'push') window.history.pushState(state, '', url);
      else window.history.replaceState(state, '', url);

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `${variant.title}. ${variant.priceFormatted}. ${variant.available ? 'Available' : 'Unavailable'}.`;
      }

      window.dispatchEvent(new CustomEvent('cv-lorex-cnx3-turret:variant-change', {
        detail: { productId: this.root.dataset.productId, variant },
      }));
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const minimum = Number(variant.quantityMin) || 1;
      const increment = Number(variant.quantityIncrement) || 1;
      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (variant.quantityMax == null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = String(variant.quantityMax);
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      if (!this.quantityInput) return;
      const minimum = Number(this.quantityInput.min) || 1;
      const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const increment = Number(this.quantityInput.step) || 1;
      const raw = Number.parseInt(this.quantityInput.value, 10);
      const safe = Number.isFinite(raw) ? raw : minimum;
      const stepped = minimum + Math.round((safe - minimum) / increment) * increment;
      this.quantityInput.value = String(Math.min(maximum, Math.max(minimum, stepped)));
      const minus = this.root.querySelector('[data-cv-lorex-cnx3-turret-quantity-minus]');
      const plus = this.root.querySelector('[data-cv-lorex-cnx3-turret-quantity-plus]');
      if (minus) minus.disabled = Number(this.quantityInput.value) <= minimum;
      if (plus) plus.disabled = Number(this.quantityInput.value) >= maximum;
    }

    stepQuantity(direction) {
      if (!this.quantityInput) return;
      const increment = Number(this.quantityInput.step) || 1;
      const current = Number.parseInt(this.quantityInput.value, 10) || Number(this.quantityInput.min) || 1;
      this.quantityInput.value = String(current + direction * increment);
      this.normalizeQuantity();
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateHero(variant) {
      if (!this.heroImage || !variant.image) return;
      this.heroImage.src = variant.image;
      if (variant.srcset) this.heroImage.srcset = variant.srcset;
      else this.heroImage.removeAttribute('srcset');
      this.heroImage.alt = variant.alt || '';

    }

    setText(selector, value) {
      const node = this.root.querySelector(selector);
      if (node) node.textContent = value ?? '';
    }

    openGallery(trigger) {
      if (!this.dialog || !this.gallery.length) return;
      this.returnFocus = trigger;
      const color = (this.currentVariant?.options[0] || '').toLowerCase();
      const matching = this.gallery.findIndex((item) => item.alt.toLowerCase().startsWith(color));
      this.showGallery(matching < 0 ? 0 : matching);
      this.dialog.showModal();
      document.documentElement.classList.add('cv-lorex-cnx3-turret-dialog-open');
      this.dialog.querySelector('[data-cv-lorex-cnx3-turret-gallery-close]')?.focus();
    }

    showGallery(index) {
      if (!this.gallery.length || !this.dialogImage) return;
      this.galleryIndex = (index + this.gallery.length) % this.gallery.length;
      const item = this.gallery[this.galleryIndex];
      this.dialogImage.src = item.src;
      this.dialogImage.alt = item.alt || '';
      if (this.dialogCaption) {
        this.dialogCaption.textContent = item.caption || '';
        this.dialogCaption.hidden = !item.caption;
      }
      const position = this.dialog.querySelector('[data-cv-lorex-cnx3-turret-gallery-position]');
      if (position) position.textContent = `${this.galleryIndex + 1} / ${this.gallery.length}`;
    }

    trapFocus(event) {
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => !node.hidden && node.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    closeGallery() {
      if (this.dialog?.open) this.dialog.close();
    }

    destroy() {
      this.abort.abort();
      this.loadingObserver?.disconnect();
      this.errorObserver?.disconnect();
      clearTimeout(this.loadingFallback);
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(selector) ? [scope] : [...scope.querySelectorAll(selector)];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new CVLorexCNX3TurretPurchase(root));
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(selector) ? event.target : event.target.querySelector?.(selector);
    if (!root) return;
    controllers.get(root)?.destroy();
    controllers.delete(root);
  });
})();
